import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import BrandLogoImage from '../../branding/BrandLogoImage';
import { identityApi } from '../../api/client';
import { useBranding } from '../../branding/portalBrandingState';
import { resolveAuthPresentation } from '../../branding/brandingPresentation';
import { ROUTES } from '../../routes/constants';

export default function RegisterPage() {
  const navigate = useNavigate();
  const presentation = resolveAuthPresentation(useBranding());

  const [form, setForm] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password) {
      setError('Name, email, and password are required.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      // Ownership is assigned server-side from the trusted domain resolver. Never send
      // operator_id, a hostname, or any branding field in the registration payload.
      const payload: Record<string, string> = {
        full_name: form.full_name,
        email:     form.email,
        password:  form.password,
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();

      await identityApi.register(payload);
      navigate(`${ROUTES.LOGIN}?verifyEmail=1`, { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-font-body min-h-screen flex items-center justify-center bg-bg-base px-4 py-3 relative overflow-hidden">

      {/* Ambient background glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-brand/[0.06] blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-info/[0.05] blur-[100px]" />
        <div className="absolute top-1/2 -left-32 w-[350px] h-[350px] rounded-full bg-bull/[0.03] blur-[80px]" />
      </div>

      <div className="w-full max-w-md relative z-10">

        {/* Logo — the bundled AlgoSys logo on the main AlgoSys host; elsewhere the Operator's
            loaded logo, a neutral placeholder while loading, or nothing. A branded portal
            shows its business name separately. */}
        <div className="text-center mb-5">
          <BrandLogoImage
            logoSrc={presentation.logoSrc}
            alt={presentation.logoAlt}
            fallback={presentation.logoFallback}
            className="h-32 mx-auto object-contain"
          />
          {presentation.showBusinessNameBadge && (
            <p
              className="auth-font-heading mt-2 truncate text-base font-semibold text-brand"
              title={presentation.businessName ?? undefined}
            >
              {presentation.businessName}
            </p>
          )}
          <p className="text-2xs text-txt-muted tracking-widest uppercase mt-2">
            Create your account
          </p>
        </div>

        {/* Card */}
        <form
          onSubmit={handleSubmit}
          className="relative bg-bg-panel border border-bg-border/60 rounded-xl p-5 space-y-3.5 shadow-modal overflow-hidden"
        >
          {/* Top brand accent line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand/50 to-transparent" />

          <p className="auth-font-heading text-center text-xs font-semibold leading-relaxed text-txt-primary">
            14-day free trial · No credit card required · Connect your preferred broker in minutes
          </p>

          {[
            { label: 'Full Name',  field: 'full_name', type: 'text',     placeholder: 'Full name',                    required: true  },
            { label: 'Email',      field: 'email',     type: 'email',    placeholder: 'Email address',                required: true  },
            { label: 'PHONE (Whatsapp Preferred)', field: 'phone', type: 'tel', placeholder: 'Phone / WhatsApp number', required: false },
            { label: 'Password',   field: 'password',  type: 'password', placeholder: 'Password, min 8 characters',   required: true  },
          ].map(({ label, field, type, placeholder, required }) => (
            <div key={field} className="space-y-1.5">
              <label className="text-2xs text-txt-label uppercase tracking-widest">
                {label}{required ? ' *' : ''}
              </label>
              <input
                type={type}
                value={(form as any)[field]}
                onChange={update(field)}
                placeholder={placeholder}
                className="input-base"
                disabled={loading}
              />
            </div>
          ))}

          {error && (
            <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-lg px-3 py-2">{error}</p>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-1">
            {loading ? 'Creating account…' : 'Start My Free Trial →'}
          </button>

          <p className="text-center text-2xs leading-snug text-txt-muted">
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </p>

          <div className="border-t border-bg-border/40 pt-3 text-center text-xs text-txt-muted">
            Already have an account?{' '}
            <Link to={ROUTES.LOGIN} className="text-brand transition-colors hover:text-brand/80">Sign in</Link>
          </div>
        </form>

        <p className="mt-4 text-center text-2xs text-txt-muted">
          {presentation.footerText}
        </p>

      </div>
    </div>
  );
}
